import { PropsWithChildren, cloneElement, isValidElement, ReactElement, useMemo } from 'react';
import 'twin.macro';
import tw, { styled } from 'twin.macro';
import { useTranslation } from 'next-i18next';
import { atom, useAtom } from 'jotai';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';

import { ButtonV2, Title, BaseModal } from '@restworld/ui-ds';

import { IEmployerFilterForm } from '../job-listing/employer-job-listing-filters';

export const toggleEmployerFiltersModalAtom = atom(false);
export const useToggleEmployerFiltersModal = () => {
  const [, setShowEmployerFiltersModal] = useAtom(toggleEmployerFiltersModalAtom);
  const closeEmployerFiltersModal = () => setShowEmployerFiltersModal(false);
  return {
    closeEmployerFiltersModal,
  };
};
export const EmployerFiltersModal = ({
  children,
  filterCount,
  formRef,
}: PropsWithChildren<{
  filterCount?: number;
  formRef?: React.RefObject<IEmployerFilterForm>;
}>) => {
  const { t } = useTranslation('common');
  const [showEmployerFiltersModal, setShowEmployerFiltersModal] = useAtom(toggleEmployerFiltersModalAtom);
  const open = () => setShowEmployerFiltersModal(true);
  const close = () => setShowEmployerFiltersModal(false);

  const isChildWithCloseProp = useMemo(() => checkChildWithCloseProp(children), [children]);

  return (
    <>
      <BaseModal
        open={showEmployerFiltersModal}
        onClose={close}
        variant="right"
        modalStyles={[tw`w-full md:(w-fit max-w-3xl overflow-y-hidden pb-0)`]}
        title={
          <Title
            fontSize="large"
            fontWeight="medium"
            content={t('filter')}
            wrapperStyle={tw`lg:(min-w-[14rem] right-8) relative`}
          />
        }
        footer={
          <ButtonsWrapper>
            <ButtonV2 tw="flex-1" onClick={() => formRef.current?.applyFilters()}>
              {t('apply_filters')}
            </ButtonV2>
            <ButtonV2
              tw="flex-1"
              variant="tertiary"
              onClick={() => formRef?.current?.removeFilters?.()}
              disabled={!filterCount}
            >
              {t('remove_filters')}
            </ButtonV2>
          </ButtonsWrapper>
        }
      >
        {isChildWithCloseProp ? cloneElement(children as ReactElement, { close }) : children}
      </BaseModal>
      <div tw="w-fit pl-0 lg:(pr-1) relative border-primary-800">
        <ButtonV2 onClick={open} variant="tertiary" StartIcon={AdjustmentsHorizontalIcon} indicator={filterCount}>
          {t('filter')}
        </ButtonV2>
      </div>
    </>
  );
};

export default EmployerFiltersModal;

interface ChildWithCloseProp {
  close: () => void;
}

const checkChildWithCloseProp = (child: unknown): child is ReactElement & ChildWithCloseProp => {
  return isValidElement(child);
};

const ButtonsWrapper = styled.div`
  ${tw`flex gap-4 w-full`}
`;
